/**
 * TASK-931: one authenticated PDF download, used by every surface that offers one.
 *
 * The risk-profile form already had this as a local helper; `FormsSubTab` toasted "Risk Profile
 * PDF coming soon" beside it, over endpoints that had existed the whole time. Copying the
 * helper across would have been the third place that knows how a gateway PDF is fetched — the
 * auth headers, the blob, the anchor, the object-URL cleanup — and the first place to drift.
 */
import { API_CONFIG } from '../config/api-config';

/**
 * Fetch a gateway-relative PDF url with the caller's credentials and save it.
 *
 * `pdfUrl` is what the generate endpoints return (`/api/risk-profile/{id}/pdf/...`), i.e.
 * gateway-relative — it is joined to the gateway base rather than used as an absolute link,
 * because a plain `<a href>` carries no Authorization header and would 401.
 */
export async function downloadPdfWithAuth(pdfUrl: string, filename: string): Promise<void> {
  const token = localStorage.getItem('auth_token');
  const accountId = localStorage.getItem('account_id');

  const response = await fetch(`${API_CONFIG.GATEWAY_URL}${pdfUrl}`, {
    method: 'GET',
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(accountId && { 'X-Account-ID': accountId }),
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to download PDF: ${response.statusText}`);
  }

  const blobUrl = window.URL.createObjectURL(await response.blob());
  try {
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    // In a `finally`, because a throw between createObjectURL and revoke leaks the blob for
    // the life of the document — the original helper revoked only on the success path.
    window.URL.revokeObjectURL(blobUrl);
  }
}
